"use client";

import { useLayoutEffect, useRef, useState } from "react";

export type TemplateOpeningScreenDecision = boolean | null;

let hasTemplateRouteMountedInDocument = false;

export function shouldShowTemplateOpeningScreen(
  navigationType: string | null | undefined,
  navigationUrl?: string,
  currentUrl?: string,
  hasMountedInDocument = false,
): boolean {
  if (hasMountedInDocument) {
    return true;
  }

  if (navigationType !== "reload") {
    return true;
  }

  if (!navigationUrl || !currentUrl) {
    return false;
  }

  return navigationUrl.split("#", 1)[0] !== currentUrl.split("#", 1)[0];
}

export function keepTemplateOpeningScreenDecision(
  currentDecision: TemplateOpeningScreenDecision,
  nextDecision: boolean,
): boolean {
  return currentDecision ?? nextDecision;
}

export function templateOpeningContentIsVisible(
  decision: TemplateOpeningScreenDecision,
  openingCompleted: boolean,
): boolean {
  return decision === false
    || (decision === true && openingCompleted);
}

export function useTemplateOpeningScreen(): TemplateOpeningScreenDecision {
  const [showOpeningScreen, setShowOpeningScreen] =
    useState<TemplateOpeningScreenDecision>(null);
  const decisionRef = useRef<TemplateOpeningScreenDecision>(null);

  useLayoutEffect(() => {
    if (decisionRef.current !== null) {
      return;
    }

    const navigation = window.performance
      .getEntriesByType("navigation")[0] as
      | PerformanceNavigationTiming
      | undefined;

    const decision = keepTemplateOpeningScreenDecision(
      decisionRef.current,
      shouldShowTemplateOpeningScreen(
        navigation?.type,
        navigation?.name,
        window.location.href,
        hasTemplateRouteMountedInDocument,
      ),
    );

    hasTemplateRouteMountedInDocument = true;
    decisionRef.current = decision;
    setShowOpeningScreen(decision);
  }, []);

  return showOpeningScreen;
}
